import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { DepartamentoSelect } from "@/components/DepartamentoSelect";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { pontoApi, type CargoPonto } from "@/features/ponto/pontoApi";

const VAZIO = { Id: 0, SNome: "", SCbo: "", IdDepartamento: 0, FSalarioBase: "", IbAtivo: true };
const chave = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const moeda = (v: number | null) => (v == null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

/** Cadastro de cargos/funções do RH — porta do FrmCadCargo (CBO, salário base, departamento padrão). */
export default function CargosPage() {
  const qc = useQueryClient();
  const { data: itens = [], isLoading } = useQuery({ queryKey: ["ponto-cargos"], queryFn: () => pontoApi.cargos(false) });
  const [busca, setBusca] = useState("");
  const [somenteAtivos, setSomenteAtivos] = useState(true);
  const [departamentoId, setDepartamentoId] = useState<number | undefined>();
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<any>(VAZIO);
  const [excluir, setExcluir] = useState<CargoPonto | null>(null);

  const filtrados = useMemo(() => {
    const b = chave(busca.trim());
    return itens.filter((c) => (!somenteAtivos || c.ibAtivo)
      && (departamentoId === undefined || c.idDepartamento === departamentoId)
      && (!b || chave(c.sNome).includes(b) || c.sCbo.includes(b)));
  }, [itens, busca, somenteAtivos, departamentoId]);
  const pag = usePaginacaoLocal(filtrados);

  const recarregar = () => {
    qc.invalidateQueries({ queryKey: ["ponto-cargos"] });
    qc.invalidateQueries({ queryKey: ["ponto-funcionarios-lista"] });
  };

  const salvar = async () => {
    if (!form.SNome.trim()) { toast.warning("Informe o nome."); return; }
    const salario = String(form.FSalarioBase).trim();
    try {
      await apiClient.post("/api/rh/ponto/cargos", {
        ...form,
        // "1.500,50" (pt-BR) ou "1500.50"
        FSalarioBase: salario === "" ? null : Number(salario.includes(",") ? salario.replace(/\./g, "").replace(",", ".") : salario),
      });
      toast.success("Cargo salvo."); setDialog(false); recarregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao salvar."); }
  };

  const confirmarExclusao = async () => {
    if (!excluir) return;
    try {
      const r = await apiClient.delete(`/api/rh/ponto/cargos/${excluir.id}`);
      toast.success(r.data?.mensagem ?? "Excluído."); setExcluir(null); recarregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao excluir."); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Cargos</h1>
          <p className="text-sm text-muted-foreground">Funções usadas no cadastro de funcionários e nos filtros do RH.</p>
        </div>
        <Button onClick={() => { setForm(VAZIO); setDialog(true); }}><Plus className="h-4 w-4 mr-2" />Novo</Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Nome ou CBO" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <DepartamentoSelect value={departamentoId} onChange={setDepartamentoId} rotuloTodos="Todos os departamentos" className="w-full sm:w-64" />
        <div className="flex items-center gap-2">
          <Switch checked={somenteAtivos} onCheckedChange={setSomenteAtivos} id="cargo-ativos" />
          <Label htmlFor="cargo-ativos">Somente ativos</Label>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>CBO</TableHead>
              <TableHead>Departamento padrão</TableHead>
              <TableHead className="text-right">Salário base</TableHead>
              <TableHead className="text-right">Funcionários</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : pag.itensPaginados.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhum cargo.</TableCell></TableRow>
            ) : pag.itensPaginados.map((c) => (
              <TableRow key={c.id} className={!c.ibAtivo ? "opacity-50" : undefined}>
                <TableCell>{c.sNome}{!c.ibAtivo && <Badge variant="outline" className="ml-2">inativo</Badge>}</TableCell>
                <TableCell>{c.sCbo || "—"}</TableCell>
                <TableCell>{c.departamentoNome || "—"}</TableCell>
                <TableCell className="text-right">{moeda(c.fSalarioBase)}</TableCell>
                <TableCell className="text-right">{c.qtdFuncionarios}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" aria-label="Editar" onClick={() => {
                    setForm({
                      Id: c.id, SNome: c.sNome, SCbo: c.sCbo, IdDepartamento: c.idDepartamento,
                      FSalarioBase: c.fSalarioBase == null ? "" : String(c.fSalarioBase).replace(".", ","), IbAtivo: c.ibAtivo,
                    });
                    setDialog(true);
                  }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" aria-label="Excluir" onClick={() => setExcluir(c)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <PaginacaoRodape {...pag} onPaginaChange={pag.setPagina} onTamanhoPaginaChange={pag.setTamanhoPagina} />

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.Id ? "Editar cargo" : "Novo cargo"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1 col-span-2">
              <Label>Nome</Label>
              <Input value={form.SNome} maxLength={80} onChange={(e) => setForm({ ...form, SNome: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>CBO</Label>
              <Input value={form.SCbo} maxLength={6} inputMode="numeric" placeholder="6 dígitos"
                onChange={(e) => setForm({ ...form, SCbo: e.target.value.replace(/\D/g, "") })} />
            </div>
            <div className="space-y-1">
              <Label>Salário base</Label>
              <Input value={form.FSalarioBase} inputMode="decimal" placeholder="0,00"
                onChange={(e) => setForm({ ...form, FSalarioBase: e.target.value })} />
            </div>
            <div className="space-y-1 col-span-2">
              <Label>Departamento padrão</Label>
              <DepartamentoSelect value={form.IdDepartamento} onChange={(v) => setForm({ ...form, IdDepartamento: v ?? 0 })} rotuloNenhum="Nenhum" />
              <p className="text-xs text-muted-foreground">Sugerido ao escolher este cargo no cadastro do funcionário.</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.IbAtivo} onCheckedChange={(v) => setForm({ ...form, IbAtivo: v })} />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={salvar}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={excluir != null} onOpenChange={(o) => !o && setExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir o cargo {excluir?.sNome}?</AlertDialogTitle>
            <AlertDialogDescription>
              {excluir && excluir.qtdFuncionarios > 0
                ? `Há ${excluir.qtdFuncionarios} funcionário(s) com este cargo: ele será apenas inativado.`
                : "O cargo será excluído."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarExclusao}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
